/**
 * EnhancedPortalWithAI.tsx
 * Portal with AI agent animation and subtle concentric circles that respond to scroll
 * Replicates the production deployment with improved subtlety
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring, useAnimation } from 'framer-motion';
import { 
  Brain, Sparkles, Circle, ChevronDown, Globe,
  Navigation, Zap, Bot, Activity, Layers,
  Send, X, MessageCircle, Heart, RefreshCw, Users
} from 'lucide-react';
import { useLocation, Link } from 'wouter';
import { useLanguage, LanguageCode } from '../../contexts/LanguageContext';
import { betaJourneyService } from '../../services/BetaJourneyService';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import { RED_HELICOPTER_CORE, getCoachResponse } from '../../config/RedHelicopterCoachConfig';
import { UserProfile } from '../UserProfile';
// import { geminiService } from '../../services/GeminiService'; // Will enable when API key is configured

interface ConcentricCircle {
  id: number;
  size: number;
  opacity: number;
  strokeWidth: number;
  animationDelay: number;
  color: string;
}

interface AIAgent {
  x: number;
  y: number;
  scale: number;
  rotation: number;
  pulseIntensity: number;
}

const EnhancedPortalWithAI: React.FC = () => {
  const [, setLocation] = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const { user } = useAuth();
  const { isAuthenticated } = useSupabaseAuth();
  const [aiAgentState, setAiAgentState] = useState<'idle' | 'thinking' | 'active'>('idle');
  const [chatOpen, setChatOpen] = useState(false); // Minimized by default
  const [messages, setMessages] = useState<Array<{role: 'ai' | 'user', content: string}>>([
    { role: 'ai', content: RED_HELICOPTER_CORE.responseTemplates.welcome(user?.email?.split('@')[0]) }
  ]);
  // Removed controlled input state to prevent re-render issues
  const containerRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Removed unnecessary ref - using state directly
  const { scrollY, scrollYProgress } = useScroll();
  const controls = useAnimation();
  
  // Mouse tracking for AI agent
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothMouseX = useSpring(mouseX, { stiffness: 50, damping: 30 });
  const smoothMouseY = useSpring(mouseY, { stiffness: 50, damping: 30 });

  // Scroll-based transforms for concentric circles
  const circleScale = useTransform(scrollY, [0, 500, 1000], [1, 1.5, 2]);
  const circleOpacity = useTransform(scrollY, [0, 300, 600], [0.15, 0.25, 0.1]);
  const circleRotation = useTransform(scrollY, [0, 1000], [0, 360]);

  // AI Agent transforms
  const aiScale = useTransform(scrollY, [0, 500], [1, 1.2]);
  const aiGlow = useTransform(scrollY, [0, 500], [0.5, 1]);
  const aiFloat = useTransform(scrollY, [0, 1000], [0, -50]);

  // Define concentric circles with subtle parameters
  const concentricCircles: ConcentricCircle[] = [
    { id: 1, size: 200, opacity: 0.08, strokeWidth: 0.5, animationDelay: 0, color: 'rgba(147, 51, 234, 0.3)' },
    { id: 2, size: 350, opacity: 0.06, strokeWidth: 0.5, animationDelay: 0.2, color: 'rgba(139, 92, 246, 0.25)' },
    { id: 3, size: 500, opacity: 0.05, strokeWidth: 0.5, animationDelay: 0.4, color: 'rgba(124, 58, 237, 0.2)' },
    { id: 4, size: 700, opacity: 0.04, strokeWidth: 0.5, animationDelay: 0.6, color: 'rgba(109, 40, 217, 0.15)' },
    { id: 5, size: 900, opacity: 0.03, strokeWidth: 0.5, animationDelay: 0.8, color: 'rgba(91, 33, 182, 0.1)' },
    { id: 6, size: 1100, opacity: 0.02, strokeWidth: 0.5, animationDelay: 1.0, color: 'rgba(76, 29, 149, 0.08)' },
  ];

  // Track mouse movement
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      mouseX.set((clientX - innerWidth / 2) / innerWidth * 100);
      mouseY.set((clientY - innerHeight / 2) / innerHeight * 100);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY]);

  // AI Agent state management based on scroll
  useEffect(() => {
    const unsubscribe = scrollY.on('change', (latest) => {
      if (latest < 100) {
        if (aiAgentState !== 'thinking') setAiAgentState('idle');
      } else if (latest < 500) {
        if (aiAgentState !== 'thinking') setAiAgentState('thinking');
      } else {
        if (aiAgentState !== 'thinking') setAiAgentState('active');
      }
    });
    return unsubscribe;
  }, [scrollY, aiAgentState]);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);


  // Update ref when input changes
  // Removed unnecessary useEffect for ref tracking

  const handleSendMessage = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    
    const originalMessage = input.value.trim();
    if (!originalMessage) return;
    
    const userMessage = originalMessage.toLowerCase();
    setMessages(prev => [...prev, { role: 'user', content: originalMessage }]);
    input.value = '';
    setAiAgentState('thinking');
    
    // Simulate thinking delay and generate response
    setTimeout(() => {
      // Determine user's emotional state and context
      let emotionalState: 'stuck' | 'progressing' | 'celebrating' | 'reflecting' = 'reflecting';
      
      if (userMessage.includes('struggle') || userMessage.includes('challenge') || 
          userMessage.includes('difficult') || userMessage.includes('stuck') || 
          userMessage.includes('help') || userMessage.includes('lost')) {
        emotionalState = 'stuck';
      } else if (userMessage.includes('achieve') || userMessage.includes('success') || 
                 userMessage.includes('complete') || userMessage.includes('done')) {
        emotionalState = 'celebrating';
      } else if (userMessage.includes('ready') || userMessage.includes('start') || 
                 userMessage.includes('begin') || userMessage.includes('progress')) {
        emotionalState = 'progressing';
      }
      
      // Determine current module focus
      let currentModule: 'LIFE' | 'MONEY' | 'JOY' | undefined;
      if (userMessage.includes('life') || userMessage.includes('story') || userMessage.includes('stories')) {
        currentModule = 'LIFE';
      } else if (userMessage.includes('money') || userMessage.includes('finance') || userMessage.includes('balance')) {
        currentModule = 'MONEY';
      } else if (userMessage.includes('joy') || userMessage.includes('connect') || userMessage.includes('relationship')) {
        currentModule = 'JOY';
      }
      
      // Generate response based on Red Helicopter methodology
      let response = '';
      
      // Core philosophy responses
      if (userMessage.includes('agency') || userMessage.includes('kindness') || userMessage.includes('philosophy')) {
        response = `${RED_HELICOPTER_CORE.philosophy.central}: ${RED_HELICOPTER_CORE.philosophy.meaning}. ${RED_HELICOPTER_CORE.philosophy.transformation}. `;
        const value = RED_HELICOPTER_CORE.values[Math.floor(Math.random() * RED_HELICOPTER_CORE.values.length)];
        response += value + ' ';
      }
      // Module-specific responses
      else if (currentModule) {
        const moduleData = RED_HELICOPTER_CORE.lessons[currentModule];
        response = moduleData.core + '. ';
        const insight = moduleData.insights[Math.floor(Math.random() * moduleData.insights.length)];
        response += insight + ' ';
        
        // Add module intro if they're asking about it
        if (userMessage.includes('what is') || userMessage.includes('tell me') || userMessage.includes('explain')) {
          response = RED_HELICOPTER_CORE.responseTemplates.moduleIntro[currentModule] + ' ';
        }
      }
      // Journey readiness
      else if (userMessage.includes('ready') || userMessage.includes('start') || userMessage.includes('begin')) {
        const phase = RED_HELICOPTER_CORE.phases.preFlight;
        response = phase.guidance[0] + ' ' + RED_HELICOPTER_CORE.philosophy.central + ' guides every step. ';
        response += 'You can explore our AI modules here (LIFE, MONEY, JOY) or begin the full structured journey. ';
      }
      // Import data
      else if (userMessage.includes('import') || userMessage.includes('data')) {
        response = "Great! You can import your journey data using the Import button in the bottom-right corner. It accepts JSON files from Beta or any previous journey.";
      }
      // Help and support
      else if (emotionalState === 'stuck') {
        const prompts = RED_HELICOPTER_CORE.contextualPrompts.stuck;
        response = prompts[Math.floor(Math.random() * prompts.length)] + ' ';
        const guidance = RED_HELICOPTER_CORE.phases.preFlight.guidance;
        response += guidance[Math.floor(Math.random() * guidance.length)] + ' ';
      }
      // Default: use contextual response system
      else {
        response = getCoachResponse({
          currentModule,
          emotionalState,
          userMessage: originalMessage,
          previousMessages: messages
        });
      }
      
      // Ensure response ends with engaging question if it doesn't have one
      if (!response.includes('?')) {
        const questionBank = emotionalState === 'stuck' 
          ? RED_HELICOPTER_CORE.questionBanks.challenge
          : currentModule
          ? RED_HELICOPTER_CORE.questionBanks.growth
          : RED_HELICOPTER_CORE.questionBanks.discovery;
        const question = questionBank[Math.floor(Math.random() * questionBank.length)];
        response = response.trim() + ' ' + question;
      }
      
      setMessages(prev => [...prev, { 
        role: 'ai', 
        content: response
      }]);
      setAiAgentState('active');
    }, 1500);
  }, []);

  // AI Agent Chat Component - Rendered inline to avoid recreation
  const renderAIAgentChat = isAuthenticated ? (
    <>
      {/* AI Brain Button - Bottom Left */}
      <motion.div
        className="fixed bottom-4 sm:bottom-6 left-4 sm:left-6 z-50"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.5, type: "spring" }}
      >
        <motion.button
          onClick={() => setChatOpen(!chatOpen)}
          className="relative w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          animate={{
            boxShadow: chatOpen 
              ? '0 0 30px rgba(147, 51, 234, 0.6)' 
              : '0 0 15px rgba(147, 51, 234, 0.3)'
          }}
        >
          {/* Pulsing ring */}
          <motion.div
            className="absolute inset-0 rounded-full bg-purple-500"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.5, 0, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          {/* Brain icon */}
          <div className="relative flex items-center justify-center w-full h-full">
            <Brain className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            {!chatOpen && messages.length > 1 && (
              <motion.div
                className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
            )}
          </div>
        </motion.button>

      </motion.div>

      {/* Chat Window */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            className="fixed bottom-20 sm:bottom-24 left-4 sm:left-6 right-4 sm:right-auto z-50 sm:w-96 max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-3rem)]"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 20 }}
          >
            <div className="bg-black/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-purple-500/30 overflow-hidden">
              {/* Chat Header */}
              <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 p-4 border-b border-purple-500/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-purple-400" />
                    <span className="text-white font-medium">Hover Co-Navigator</span>
                    <motion.div
                      className="w-2 h-2 bg-green-400 rounded-full"
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  </div>
                  <button
                    onClick={() => setChatOpen(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div 
                ref={chatRef}
                className="h-64 sm:h-72 md:h-80 overflow-y-auto p-3 sm:p-4 space-y-2 sm:space-y-3"
              >
                {messages.map((msg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] sm:max-w-[80%] px-3 sm:px-4 py-1.5 sm:py-2 rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-800 text-gray-200'
                      }`}
                    >
                      {msg.role === 'ai' && (
                        <div className="flex items-center gap-2 mb-1">
                          <Brain className="w-3 h-3 text-purple-400" />
                          <span className="text-xs text-purple-400">Hover Co-Navigator</span>
                        </div>
                      )}
                      <p className="text-xs sm:text-sm break-words">{msg.content}</p>
                    </div>
                  </motion.div>
                ))}
                
                {aiAgentState === 'thinking' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="bg-gray-800 px-4 py-2 rounded-2xl">
                      <div className="flex gap-1">
                        {[...Array(3)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="w-2 h-2 bg-purple-400 rounded-full"
                            animate={{
                              y: [0, -8, 0]
                            }}
                            transition={{
                              duration: 0.6,
                              delay: i * 0.1,
                              repeat: Infinity
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Input */}
              <div className="p-4 border-t border-purple-500/20">
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    defaultValue=""
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        const input = e.currentTarget;
                        const value = input.value.trim();
                        if (value) {
                          handleSendMessage();
                        }
                      }
                    }}
                    placeholder="Ask about your journey..."
                    className="flex-1 bg-gray-900/50 text-white text-sm sm:text-base px-3 sm:px-4 py-2 rounded-lg border border-purple-500/30 focus:border-purple-500 focus:outline-none placeholder-gray-500"
                    autoComplete="off"
                  />
                  <motion.button
                    onClick={handleSendMessage}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-2 sm:p-2.5 rounded-lg hover:shadow-lg transition-shadow"
                  >
                    <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  ) : null;

  // Concentric Circles Component
  const ConcentricCircles = () => (
    <div className="fixed inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
      <motion.svg
        className="absolute w-[200vw] h-[200vh]"
        style={{
          scale: circleScale,
          rotate: circleRotation,
        }}
      >
        <defs>
          <radialGradient id="circleGradient">
            <stop offset="0%" stopColor="rgba(147, 51, 234, 0.1)" />
            <stop offset="100%" stopColor="rgba(147, 51, 234, 0)" />
          </radialGradient>
        </defs>
        
        {concentricCircles.map((circle) => (
          <motion.circle
            key={circle.id}
            cx="50%"
            cy="50%"
            r={circle.size}
            fill="none"
            stroke={circle.color}
            strokeWidth={circle.strokeWidth}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{
              pathLength: 1,
              opacity: circle.opacity,
            }}
            transition={{
              pathLength: { duration: 2, delay: circle.animationDelay },
              opacity: { duration: 1, delay: circle.animationDelay }
            }}
            style={{
              opacity: circleOpacity,
            }}
          />
        ))}

        {/* Animated dashed circles */}
        {concentricCircles.slice(0, 3).map((circle) => (
          <motion.circle
            key={`dashed-${circle.id}`}
            cx="50%"
            cy="50%"
            r={circle.size + 50}
            fill="none"
            stroke={circle.color}
            strokeWidth={0.3}
            strokeDasharray="2 8"
            initial={{ opacity: 0 }}
            animate={{
              opacity: circle.opacity * 0.5,
              rotate: 360,
            }}
            transition={{
              rotate: {
                duration: 30 + circle.id * 10,
                repeat: Infinity,
                ease: "linear"
              },
              opacity: { duration: 1, delay: circle.animationDelay }
            }}
            style={{
              opacity: circleOpacity,
            }}
          />
        ))}
      </motion.svg>
    </div>
  );

  return (
    <div ref={containerRef} className="relative min-h-screen bg-gradient-to-b from-black via-purple-950 to-indigo-950 overflow-hidden">
      {/* Multi-layer Parallax Background */}
      {/* Layer 1: Ripples (farthest back) */}
      <motion.div
        className="absolute inset-0 z-0"
        style={{
          y: useTransform(scrollY, [0, 1000], [0, -300]),
        }}
      >
        <img
          src="/Ripples.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
      </motion.div>

      {/* Layer 2: Sparkles/Particles */}
      <div className="absolute inset-0 z-1">
        {[...Array(50)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      {/* Layer 3: Portal Opening Effect */}
      <motion.div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          scale: useTransform(scrollY, [0, 500], [3, 1]),
          opacity: useTransform(scrollY, [0, 200], [1, 0]),
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/50 via-transparent to-purple-900/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />
      </motion.div>

      {/* Concentric Circles Layer */}
      <ConcentricCircles />

      {/* AI Agent Chat - Bottom Left */}
      {renderAIAgentChat}

      {/* Main Content */}
      <div className="relative z-30">
        {/* Hero Section */}
        <motion.div
          className="min-h-screen flex flex-col items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          {/* Red Helicopter Logo */}
          <motion.div
            className="mb-8"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 1.5, type: "spring" }}
            style={{
              y: useTransform(scrollY, [0, 300], [0, -50]),
            }}
          >
            <img
              src="/red-helicopter-logo.png"
              alt="Red Helicopter"
              className="w-48 sm:w-56 md:w-64 lg:w-80 h-auto object-contain"
              style={{ maxHeight: '320px' }}
            />
          </motion.div>

          {/* Title with glow effect */}
          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400 mb-4 text-center px-4"
            style={{
              scale: useTransform(scrollY, [0, 300], [1, 0.8]),
              opacity: useTransform(scrollY, [0, 300], [1, 0]),
            }}
          >
            {t.title}
          </motion.h1>


          {/* Module Cards */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 sm:gap-6 mt-8 sm:mt-12 px-4 w-full max-w-4xl mx-auto justify-center items-center"
            style={{
              y: useTransform(scrollY, [0, 500], [0, -100]),
            }}
          >
            {[
              { name: t.life, image: '/helicopter-body.png', color: 'from-red-500 to-pink-500', route: '/life', desc: t.lifeDesc || 'Build your foundation' },
              { name: t.money, image: '/helicopter-tail.png', color: 'from-green-500 to-emerald-500', route: '/money', desc: t.moneyDesc || 'Power your journey' },
              { name: t.joy, image: '/Helicopter-top.png', color: 'from-yellow-500 to-orange-500', route: '/joy', desc: t.joyDesc || 'Bending Space - Time' }
            ].map((module, i) => (
              <motion.div
                key={module.name}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                onClick={() => setLocation(module.route)}
                className="cursor-pointer"
              >
                <div className="relative group">
                  {/* Card background with gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${module.color} rounded-2xl opacity-20 group-hover:opacity-30 transition-opacity`} />
                  
                  {/* Card content */}
                  <div className="relative p-4 sm:p-6 md:p-8 bg-black/50 backdrop-blur-lg rounded-2xl border border-white/10 w-full sm:min-w-[180px] md:min-w-[200px] flex flex-col items-center text-center">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mb-3 sm:mb-4 relative">
                      <img 
                        src={module.image} 
                        alt={module.name}
                        className="w-full h-full object-contain filter brightness-0 invert opacity-90 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-white">{module.name}</h3>
                    <p className="text-xs sm:text-sm text-gray-300 mt-1 sm:mt-2">{module.desc}</p>
                    
                    {/* Hover effect particles */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      {[...Array(3)].map((_, j) => (
                        <motion.div
                          key={j}
                          className="absolute w-1 h-1 bg-white rounded-full"
                          style={{
                            left: `${20 + j * 30}%`,
                            top: `${20 + j * 25}%`,
                          }}
                          animate={{
                            y: [-10, 10, -10],
                            opacity: [0, 1, 0],
                          }}
                          transition={{
                            duration: 2,
                            delay: j * 0.3,
                            repeat: Infinity,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            className="absolute bottom-6 sm:bottom-8 md:bottom-10 left-1/2 -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              opacity: useTransform(scrollY, [0, 100], [1, 0]),
            }}
          >
            <div className="text-purple-300 text-center">
              <p className="text-sm mb-2">{t.scroll}</p>
              <ChevronDown className="w-6 h-6 mx-auto" />
            </div>
          </motion.div>
        </motion.div>

      </div>

      {/* Transformation Section - Agency Framework */}
      <motion.div 
        className="min-h-screen bg-gradient-to-b from-black via-purple-950/20 to-black flex items-center justify-center px-4 sm:px-6 md:px-8 py-12 sm:py-16 md:py-20"
        style={{
          opacity: useTransform(scrollY, [200, 400], [0, 1]),
        }}
      >
        <div className="max-w-6xl mx-auto">
          {/* Core Agency Message */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-4 sm:mb-6">
              {t.toFlyFree}
            </h2>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-purple-300 mb-3 sm:mb-4 px-4">
              {t.agencyRequiresTrust}
            </p>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-purple-200 px-4">
              {t.highAgencyEarnsTrust}
            </p>
          </motion.div>

          {/* Two Main Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
            {/* What is Agency Card */}
            <motion.div
              className="p-4 sm:p-6 md:p-8 bg-gradient-to-br from-orange-900/30 to-orange-800/20 backdrop-blur-lg rounded-2xl border border-orange-500/30"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl sm:text-2xl font-bold text-orange-300 mb-4 sm:mb-6">{t.whatIsAgency}</h3>
              
              <div className="space-y-4 text-orange-100">
                <p className="text-base sm:text-lg leading-relaxed">
                  {t.agencyDescription}
                </p>
                
                <div className="mt-6 space-y-4">
                  <div className="flex items-start space-x-3 p-3 bg-orange-950/30 rounded-lg">
                    <div className="w-6 h-6 rounded-full border-2 border-orange-400 flex items-center justify-center mt-1">
                      <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-orange-200">{t.autonomy}</h4>
                      <p className="text-sm text-orange-100/80">{t.autonomyDesc}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 bg-orange-950/30 rounded-lg">
                    <Heart className="w-5 h-5 text-orange-400 mt-1" />
                    <div>
                      <h4 className="font-semibold text-orange-200">{t.intrinsicMotivation}</h4>
                      <p className="text-sm text-orange-100/80">{t.intrinsicMotivationDesc}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 bg-orange-950/30 rounded-lg">
                    <RefreshCw className="w-5 h-5 text-orange-400 mt-1" />
                    <div>
                      <h4 className="font-semibold text-orange-200">{t.competence}</h4>
                      <p className="text-sm text-orange-100/80">{t.competenceDesc}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 bg-orange-950/30 rounded-lg">
                    <Users className="w-5 h-5 text-orange-400 mt-1" />
                    <div>
                      <h4 className="font-semibold text-orange-200">{t.relatedness}</h4>
                      <p className="text-sm text-orange-100/80">{t.relatednessDesc}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Agency Requires Balance Card */}
            <motion.div
              className="p-4 sm:p-6 md:p-8 bg-gradient-to-br from-purple-900/30 to-purple-800/20 backdrop-blur-lg rounded-2xl border border-purple-500/30"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl sm:text-2xl font-bold text-purple-300 mb-3 sm:mb-4">{t.agencyRequiresBalance}</h3>
              
              <p className="text-base sm:text-lg md:text-xl font-semibold text-purple-200 mb-3 sm:mb-4">
                {t.balanceEnablesAgility}
              </p>
              
              <p className="text-sm sm:text-base md:text-lg text-purple-100 mb-4 sm:mb-6 text-center">
                {t.highAgencyCreates}
              </p>
              
              {/* Centered helicopter diagram */}
              <div className="flex justify-center mb-4 sm:mb-6 mt-4 sm:mt-6 md:mt-8">
                <img 
                  src="/Helicopter-build.jpg" 
                  alt="Helicopter Framework showing LIFE, MONEY, JOY components" 
                  className="w-full max-w-[250px] sm:max-w-[350px] md:max-w-[400px] h-auto rounded-lg shadow-md"
                />
              </div>

              <div className="space-y-4">
                <h4 className="text-base sm:text-lg md:text-xl font-semibold text-purple-200">{t.helicopterVisualize}</h4>
                <div className="space-y-2 sm:space-y-3 text-sm sm:text-base text-purple-100 leading-relaxed">
                  <div className="flex items-start space-x-3 p-2 rounded-lg bg-purple-950/30">
                    <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p>{t.helicopterAgile}</p>
                  </div>
                  <div className="flex items-start space-x-3 p-2 rounded-lg bg-purple-950/30">
                    <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p>{t.helicopterHover}</p>
                  </div>
                  <div className="flex items-start space-x-3 p-2 rounded-lg bg-purple-950/30">
                    <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p>{t.helicopterLand}</p>
                  </div>
                  <div className="flex items-start space-x-3 p-2 rounded-lg bg-purple-950/30">
                    <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p>{t.helicopterSkill}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bottom Message */}
          <motion.div
            className="mt-8 sm:mt-12 md:mt-16 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <button
              onClick={() => {
                // Launch Beta journey for authenticated users
                if (user) {
                  betaJourneyService.launchJourney(user.id, user.email || undefined);
                } else {
                  // Scroll to top for non-authenticated users to see login options
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm sm:text-base font-semibold rounded-full hover:from-purple-700 hover:to-pink-700 transition-all transform hover:scale-105"
            >
              {t.beginJourney}
            </button>
          </motion.div>
        </div>
      </motion.div>

      {/* Quick Links - Top Left - Hidden for now */}
      {/* <div className="fixed top-2 sm:top-4 left-20 sm:left-24 z-50">
        <Link 
          href="/alerislife"
          className="px-3 py-1 bg-purple-600/80 text-white text-xs sm:text-sm rounded-lg hover:bg-purple-700 transition backdrop-blur inline-block"
        >
          Alerislife IT Guide
        </Link>
      </div> */}

      {/* Navigation Controls - Top Right */}
      <div className="fixed top-2 sm:top-4 right-2 sm:right-4 z-50 flex items-center gap-2 sm:gap-4">
        {/* User Profile or Login Button */}
        {user ? (
          <UserProfile />
        ) : (
          <Link href="/login">
            <button className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium rounded-lg transition-all duration-300 shadow-lg hover:shadow-purple-500/50">
              Login
            </button>
          </Link>
        )}

        {/* Language Selector */}
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value as LanguageCode)}
          className="px-2 sm:px-4 py-1 sm:py-2 text-sm sm:text-base bg-black/50 backdrop-blur-lg text-white rounded-lg border border-white/20"
        >
          <option value="en">English</option>
          <option value="ko">한국어</option>
          <option value="es">Español</option>
        </select>
      </div>
    </div>
  );
};

export default EnhancedPortalWithAI;